import OnboardingModal from '@/components/Modals/Onboarding/onboarding-modal';
import { useAuth } from '@/context/AuthContext';

import { useUnriddle } from '@/context/UnriddleContext';
import { useIntercomSupport } from '@/hooks/useIntercomSupport';
import { useUploadToast } from '@/hooks/useUploadToast';

import useAnalytics from '@/lib/analytics/useAnalytics';

import dayjs from 'dayjs';

import Script from 'next/script';
import { useFeatureFlagEnabled } from 'posthog-js/react';

// import Banner from '../Banners/Banner';
import ImpersonationBanner from '../Banners/ImpersonationBanner';
import RewardBanner from '../Banners/RewardBanner';

import { FreeUserModal } from '../Modals/FreeUserModal';
import { UgcProgramModal } from '../Modals/UgcProgramModal';
import Upgrade from '../Modals/UpgradeModal';
import NPSModal from '../NPSModal';

import dynamic from 'next/dynamic';
const NewSearchMenu = dynamic(() => import('../New/NewSearchMenu'), {
  ssr: false,
});
const ZoteroModal = dynamic(() => import('../Modals/ZoteroModal'), {
  ssr: false,
});

import AccessRevoked from '../Settings/AccessRevoked';
import LeftSidebar from '../Sidebars/LeftSidebar';

import { useIsMobile } from '@/hooks/useIsMobile';
import { type ReactNode, useEffect } from 'react';
import UnriddleToAnaraBanner from '../Banners/UnriddleToAnaraBanner';

import { api } from '@/lib/trpc/react';
import { atom, useAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import Banner from '../Banners/Banner';
import ConfirmationDialog from '../Confirmation/ConfirmationDialog';
import SiteDropzone from '../New/SiteDropzone';
import SidebarChatsOverlay from '../Sidebars/SidebarChatsOverlay';
import { TourWrapper } from '../tour/TourWrapper';
import { AppPanels } from './AppPanels';
import { DragSidebar } from './DragSidebar';
import { ChurnekyLoader } from './churnkey-loader';

export const zoteroModalAtom = atom<boolean>(false);

export function SiteLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const ugcProgramEnabled = useFeatureFlagEnabled('ugc-program-modal');
  const freeUserModalEnabled = useFeatureFlagEnabled('free-user-modal');
  const searchParams = useSearchParams();
  const zotero = searchParams?.get('zotero');
  const utils = api.useUtils();
  const [showZoteroModal, setShowZoteroModal] = useAtom(zoteroModalAtom);

  // useVersionCheck();

  useEffect(() => {
    if (zotero === 'success') {
      toast.success('Successfully connected to Zotero');
      utils.user.byEmail.invalidate();
      setShowZoteroModal(true);
    }
    if (zotero === 'error') {
      toast.error('Failed to connect to Zotero');
    }
    if (zotero === 'existing') {
      toast.info('Zotero already connected');
    }
  }, [zotero]);

  const {
    showAccessRevokedModal,
    // setReferralId,
    showUgcProgramModal,
    showFreeUserModal,
    setShowFreeUserModal,
    setShowUgcProgramModal,
  } = useUnriddle();
  const { track } = useAnalytics();

  const isMobile = useIsMobile();

  useUploadToast();

  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const rewardfulCallback = (window as any).rewardful;
    if (!rewardfulCallback) return;

    rewardfulCallback('ready', () => {
      //@ts-expect-error find remove this if not used
      if (window.Rewardful?.referral) {
        // setReferralId(window.Rewardful.referral);
        //@ts-expect-error find remove this if not used

        console.log('Current referral ID: ', window.Rewardful?.referral);
      } else {
        // console.log('No referral present.');
      }
    });
  }, []);

  useIntercomSupport();

  useEffect(() => {
    if (
      ugcProgramEnabled &&
      user &&
      !localStorage.getItem('ugcProgramModalClosed')
    ) {
      const studyOnboarded = user.onboardingResponse?.some(
        (response) => response?.purpose?.toLowerCase() === 'study',
      );
      const userCreatedAt = dayjs(user.createdAt);
      const daysSinceCreation = dayjs().diff(userCreatedAt, 'day');
      if (daysSinceCreation > 7 && studyOnboarded) {
        setTimeout(() => {
          setShowUgcProgramModal(true);
          track('ugc_program_modal_shown');
        }, 2000);
      }
    }
  }, [user, ugcProgramEnabled]);

  useEffect(() => {
    if (
      freeUserModalEnabled &&
      user &&
      !localStorage.getItem('freeUserModalClosed')
    ) {
      setTimeout(() => {
        setShowFreeUserModal(true);
        track('free_user_modal_shown');
      }, 2000);
    }
  }, [user, freeUserModalEnabled]);

  return (
    <>
      <Script
        strategy="afterInteractive"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
        dangerouslySetInnerHTML={{
          __html: `
                (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/u9yuy4xj';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
                `,
        }}
      />

      <Script
        strategy="afterInteractive"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
        dangerouslySetInnerHTML={{
          __html: `
      !function(){  
        if (!window.churnkey || !window.churnkey.created) {
          window.churnkey = { created: true };
          const a = document.createElement('script');
          a.src = 'https://assets.churnkey.co/js/app.js?appId=rj4lblz7e';
          a.async = true;
          const b = document.getElementsByTagName('script')[0];
          b.parentNode.insertBefore(a, b);
        }
      }();
    `,
        }}
      />

      <SiteDropzone>
        <ChurnekyLoader />
        <NewSearchMenu type="dialog" />
        <Upgrade />

        {showAccessRevokedModal && <AccessRevoked redirect={undefined} />}
        <NPSModal />
        {showUgcProgramModal && <UgcProgramModal />}
        {showFreeUserModal && <FreeUserModal />}
        <div className="relative flex flex-col w-full h-dvh items-end bg-background">
          <ImpersonationBanner />
          {/* <PermanentBanner /> */}
          <Banner />
          <UnriddleToAnaraBanner />

          <RewardBanner />
          {showZoteroModal && <ZoteroModal />}
          {user?.isOnboarded === false && <OnboardingModal />}

          <ConfirmationDialog />
          <DragSidebar>
            <div className="flex w-full h-full min-h-0">
              {isMobile ? (
                <>
                  <LeftSidebar />
                  <SidebarChatsOverlay />
                  {children}
                </>
              ) : (
                <AppPanels>
                  <TourWrapper>{children}</TourWrapper>
                </AppPanels>
              )}
            </div>
          </DragSidebar>
        </div>
      </SiteDropzone>
    </>
  );
}
